import React from 'react';
import Sparkline from './Sparkline';
import {
  monthName, previousMonth, seconds, secondsBare, shortTime, weekNumber,
} from './engagementHelpers';

/*
 * Tab "Valmistajahaut" (OVIO-103, Pencil frames 05 and 06): the Ezyvin pilot's queries per workshop and
 * period, and one workshop's month report with its press log. Paid = what the workshop's limit counts.
 */
const BOLD = 'TitilliumWeb-SemiBold';
const BLUE = '#0095DA';
const LIGHT = '#9ED8F2';
const GREY = '#D5D9DE';
const AMBER = '#C25E00';
const RED = '#D32F2F';
const th = {
  fontSize: 13, fontWeight: 'normal', color: '#4A5560', padding: '10px 5px', borderBottom: '1px solid #E3E6EA',
  textAlign: 'left', verticalAlign: 'bottom', lineHeight: '16px',
};
const td = { fontSize: 15, color: '#222', padding: '0 5px', height: 48, borderBottom: '1px solid #E3E6EA' };
const num = { textAlign: 'right', whiteSpace: 'nowrap' };
const OUTCOME_KEYS = ['found', 'no_data', 'error', 'timeout', 'limit'];
// Log kinds follow the limit's counting: paid counts, a reuse is free, free = an own press that counts nothing.
const LOG_KIND = { paid: 'logPaid', reuse: 'logReuse', free: 'logFree' };
// The workshop name reads as plain text but is focusable.
const NAME_BUTTON = {
  border: 0, background: 'none', padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer',
};
// Chart labels as in frame 05.
const SHORT_MONTHS = ['tammi', 'helmi', 'maalis', 'huhti', 'touko', 'kesä', 'heinä', 'elo', 'syys', 'loka', 'marras', 'joulu'];

const sumOutcomes = o => OUTCOME_KEYS.reduce((s, k) => s + o[k], 0) + o.pending;
const outcomeLine = o => OUTCOME_KEYS.map(k => o[k]).join(' · ');
const percent = ({ n, of }) => (of ? Math.round((100 * n) / of) + ' %' : '–');
const pair = (t1, unit) => (t1.median === null ? '–' : t1.median + ' ' + unit + ' / ' + t1.p95 + ' ' + unit);

function limitLevel(paid, limit) {
  if (limit && paid >= limit) {
    return 'full';
  }
  if (limit && paid >= Math.ceil(0.8 * limit)) {
    return 'high';
  }
  return 'ok';
}

function Tile({
  label, value, note, accent, marker,
}) {
  return (
    <div
      data-reconciliation={marker}
      style={{
        flex: '1 1 0', minWidth: 0, background: '#F7F8FA', border: '1px solid #E3E6EA', borderRadius: 4, padding: '12px 14px',
        marginRight: 10, borderLeft: accent ? '4px solid ' + accent : '1px solid #E3E6EA',
      }}
    >
      <div style={{ fontSize: 13, color: '#4A5560' }}>{label}</div>
      <div style={{
        fontSize: 22, fontFamily: 'TitilliumWeb-Bold', color: '#222', margin: '4px 0', whiteSpace: 'nowrap',
      }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, color: '#5F6B76', lineHeight: '17px' }}>{note}</div>
    </div>
  );
}

function reconciliationTile(r, t) {
  const by = {
    match: [r.ezyvin + ' = ' + r.ovio, '#2E7D32'],
    pending: [r.ezyvin + ' ≈ ' + r.ovio, '#9AA3AB'],
    mismatch: [r.ezyvin + ' ≠ ' + r.ovio, AMBER],
    unreadable: [t('reconcileUnreadable'), '#9AA3AB'],
    stale: [t('reconcileStale'), '#9AA3AB'],
    unchecked: [t('reconcileUnchecked'), '#9AA3AB'],
  };
  return by[r.status] || by.unchecked;
}

export function BarChart({ bars, t, big }) {
  const height = big ? 190 : 130;
  const max = Math.max(1, ...bars.map(b => b.paid + b.reuse));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', height: height + 40 }}>
      {bars.map(bar => (
        <div
          key={bar.label}
          data-chart-bar={bar.label}
          data-partial={bar.partial ? 'true' : 'false'}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 3px' }}
        >
          {big && (
            <div style={{ fontSize: 13, fontFamily: BOLD, marginBottom: 3 }}>
              {bar.reuse ? bar.paid + ' + ' + bar.reuse : bar.paid}
            </div>
          )}
          <div style={{
            width: big ? '60%' : '80%', height: Math.round((bar.reuse / max) * height), background: bar.partial ? GREY : LIGHT,
          }}
          />
          <div style={{
            width: big ? '60%' : '80%', height: Math.max(2, Math.round((bar.paid / max) * height)), background: bar.partial ? GREY : BLUE,
          }}
          />
          <div style={{ fontSize: 12, color: '#5F6B76', marginTop: 4, whiteSpace: 'nowrap' }}>
            {bar.label.indexOf('-W') > 0 ? weekNumber(bar.label) : SHORT_MONTHS[parseInt(bar.label.split('-')[1], 10) - 1] + (bar.partial ? '*' : '')}
          </div>
        </div>
      ))}
      <span style={{ display: 'none' }}>{t('chartLegend')}</span>
    </div>
  );
}

function LimitBar({ paid, limit }) {
  const level = limitLevel(paid, limit);
  const colour = { full: RED, high: AMBER, ok: BLUE }[level];
  const share = limit ? Math.min(1, paid / limit) : 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
      <span data-limit-level={level} style={{ display: 'inline-block', width: 70, height: 8, background: '#E3E6EA', borderRadius: 4, marginRight: 8 }}>
        <span style={{ display: 'block', width: Math.round(share * 70), height: 8, background: colour, borderRadius: 4 }} />
      </span>
      <span style={{ color: level === 'ok' ? '#222' : colour }}>{paid + ' / ' + limit}</span>
    </span>
  );
}

function EzyvinList({
  data, t, onWorkshop, onPeriod,
}) {
  const { tiles, total } = data;
  const [recValue, recColour] = reconciliationTile(tiles.reconciliation, t);
  const month = monthName(data.month);
  const toggle = period => ({
    padding: '0 16px', height: 40, border: '1px solid ' + BLUE, cursor: 'pointer', fontSize: 15, fontFamily: 'inherit',
    background: data.period === period ? BLUE : '#fff', color: data.period === period ? '#fff' : '#222',
  });
  return (
    <div>
      <div style={{ display: 'flex', marginTop: 18 }}>
        <button type='button' data-period='month' aria-pressed={data.period === 'month'} style={toggle('month')} onClick={() => onPeriod('month')}>
          {t('periodMonth')}
        </button>
        <button type='button' data-period='week' aria-pressed={data.period === 'week'} style={toggle('week')} onClick={() => onPeriod('week')}>
          {t('periodWeek')}
        </button>
      </div>
      <div style={{ display: 'flex', marginTop: 16 }}>
        <Tile
          label={t('tilePaid', { month })}
          value={tiles.paid}
          note={t('tilePaidNote', { previous: tiles.paid_previous, month: monthName(previousMonth(data.month)) })}
        />
        <Tile label={t('tileReuse')} value={tiles.reuse} note={t('tileReuseNote')} />
        <Tile label={t('tileOutcomes')} value={outcomeLine(tiles.outcomes)} note={t('tileOutcomesNote', { pending: tiles.outcomes.pending })} />
        <Tile label={t('tileButton')} value={seconds(tiles.button.median)} note={t('tileButtonNote', { p95: seconds(tiles.button.p95) })} />
        <Tile
          label={t('tileReconcile', { month })}
          value={recValue}
          accent={recColour}
          marker={tiles.reconciliation.status}
          note={t('tileReconcileNote', { billed: tiles.billed, without: tiles.without_query })
            + (tiles.reconciliation.checked_at ? ' · ' + t('checkedAt', { time: shortTime(tiles.reconciliation.checked_at) }) : '')}
        />
      </div>
      <div style={{ border: '1px solid #E3E6EA', borderRadius: 4, padding: '16px 18px', marginTop: 16 }}>
        <div style={{ fontSize: 15, fontFamily: BOLD, marginBottom: 6 }}>
          {t(data.period === 'week' ? 'chartWeeks' : 'chartMonths')}
        </div>
        <BarChart bars={data.chart} t={t} big={data.period !== 'week'} />
        <div style={{ fontSize: 12, color: '#5F6B76', marginTop: 6 }}>{t('chartNote')}</div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
          <thead>
            <tr>
              <th style={th}>{t('colWorkshop')}</th>
              <th style={th}>{t('colLimit', { month })}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('colPaid')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('colReuse')}</th>
              <th style={th}>{t('colOutcomes')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('colButton')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('colOpenedShare')}</th>
              <th style={th}>{t('colQueries26')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const none = sumOutcomes(row.outcomes) === 0;
              const slow = row.button.p95 !== null && row.button.p95 > 2000;
              return (
                <tr key={row.id} onClick={() => onWorkshop(String(row.id))} style={{ cursor: 'pointer' }}>
                  <td style={td}>
                    {/* A button so Tab and Enter open the report; its click reaches the row's handler. */}
                    <button type='button' style={NAME_BUTTON}>{row.name}</button>
                  </td>
                  <td style={td}><LimitBar paid={row.paid} limit={row.limit} /></td>
                  <td style={{ ...td, ...num }}>{row.paid}</td>
                  <td style={{ ...td, ...num }}>{row.reuse}</td>
                  <td style={td}>{none ? '–' : outcomeLine(row.outcomes)}</td>
                  <td style={{ ...td, ...num, color: slow ? AMBER : '#222' }}>
                    {row.button.median === null ? '–' : secondsBare(row.button.median) + ' / ' + seconds(row.button.p95)}
                  </td>
                  <td style={{ ...td, ...num }}>{percent(row.opened)}</td>
                  <td style={td}><Sparkline values={row.series} weeks={data.weeks} label={row.name + ': ' + t('colQueries26')} /></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#F7F8FA', fontFamily: BOLD }}>
              <td style={td}>{t('totalRow', { count: total.workshops })}</td>
              <td style={{ ...td, color: AMBER }}>{t('atLimit', { count: total.at_80 })}</td>
              <td style={{ ...td, ...num }}>{total.paid}</td>
              <td style={{ ...td, ...num }}>{total.reuse}</td>
              <td style={td}>{outcomeLine(total.outcomes)}</td>
              <td style={{ ...td, ...num }}>
                {total.button.median === null ? '–' : secondsBare(total.button.median) + ' / ' + seconds(total.button.p95)}
              </td>
              <td style={{ ...td, ...num }}>{percent(total.opened)}</td>
              <td style={td}><Sparkline values={total.series} weeks={data.weeks} label={t('colQueries26')} /></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p style={{ fontSize: 13, color: '#5F6B76', lineHeight: '20px', marginTop: 20 }}>{t('helpEzyvin')}</p>
    </div>
  );
}

const CHIP = {
  found: ['#D9F5E1', '#1E6B34'], no_data: ['#ECEEF0', '#3D4650'], error: ['#FDE2E2', '#B71C1C'],
  timeout: ['#FDE2E2', '#B71C1C'], limit: ['#FFF1C7', '#8A5300'], pending: ['#E3F2FB', '#0B5E8A'],
};

function EzyvinMonth({ data, t, onWorkshop }) {
  const { report, reconciliation: rec } = data;
  const month = monthName(data.month);
  const share = report.limit ? Math.round((100 * report.paid) / report.limit) : 0;
  const line = (label, value, warn) => (
    <div style={{ display: 'flex', fontSize: 14, padding: '5px 0' }}>
      <span style={{ flex: 1, color: '#4A5560' }}>{label}</span>
      <span style={{ fontFamily: BOLD, color: warn ? AMBER : '#222' }}>{value}</span>
    </div>
  );
  const button = report.button.median === null ? '–' : seconds(report.button.median) + ' / ' + seconds(report.button.p95);
  return (
    <div>
      {rec.status === 'mismatch' && (
        <div
          role='alert'
          style={{
            marginTop: 18, padding: '12px 16px', background: '#FFF3CD', borderLeft: '4px solid ' + AMBER, borderRadius: 4,
            fontSize: 14, color: '#7A3E00', lineHeight: '22px',
          }}
        >
          <div style={{ fontFamily: BOLD }}>
            {t('mismatchHeading', {
              ezyvin: rec.ezyvin, ovio: rec.ovio, difference: rec.difference, in_flight: rec.in_flight,
            })}
          </div>
          <div>{t('mismatchText', { difference: rec.difference, in_flight: rec.in_flight })}</div>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <button
          type='button'
          data-back
          onClick={() => onWorkshop('')}
          style={{
            height: 40, padding: '0 16px', border: '1px solid #C9CED4', borderRadius: 4, background: '#fff', cursor: 'pointer',
            fontSize: 15, fontFamily: 'inherit',
          }}
        >
          {'← ' + t('backToAll')}
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 16 }}>
        <div style={{
          flex: '1 1 340px', border: '1px solid #E3E6EA', borderRadius: 4, padding: '14px 18px', marginRight: 14, marginBottom: 14,
        }}
        >
          <div style={{ fontSize: 16, fontFamily: BOLD, marginBottom: 8 }}>{data.workshop.name + ' · ' + month + ' ' + data.month.split('-')[0]}</div>
          {line(t('reportPaid'), report.paid + ' / ' + report.limit + ' (' + share + ' %)')}
          {line(t('reportReuse'), report.reuse)}
          {line(t('reportOutcomes'), outcomeLine(report.outcomes) + (report.outcomes.pending ? ' · ' + t('pendingCount', { count: report.outcomes.pending }) : ''))}
          {line(t('reportAccepted'), report.accepted.n + ' / ' + report.accepted.of)}
          {line(t('reportButton'), button, report.button.p95 !== null && report.button.p95 > 2000)}
          {line(t('reportQuery'), pair(report.query, 's'))}
          {line(t('reportOpenGap'), pair(report.open_gap, 'min'))}
          {line(t('reportOpened'), report.opened.of ? report.opened.n + ' / ' + report.opened.of + ' (' + percent(report.opened) + ')' : '–')}
        </div>
        <div style={{ flex: '1 1 340px', border: '1px solid #E3E6EA', borderRadius: 4, padding: '14px 18px', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontFamily: BOLD, marginBottom: 8 }}>{t('chartWeeks')}</div>
          <BarChart bars={data.weekly} t={t} />
        </div>
      </div>
      <div style={{ fontSize: 16, fontFamily: BOLD, margin: '10px 0 4px' }}>
        {t('logHeading', { month: month + ' ' + data.month.split('-')[0], count: data.log.length })}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['logTime', 'logCar', 'logKind', 'logAccepted', 'logOutcome', 'logButton', 'logQuery', 'logOpened', 'logGap']
                .map(key => <th key={key} style={th}>{t(key)}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.log.map(entry => (
              <tr key={entry.id}>
                <td style={td}>{shortTime(entry.time)}</td>
                <td style={td}>{entry.plate}</td>
                <td style={td}>{t(LOG_KIND[entry.kind])}</td>
                <td style={td}>{entry.accepted === null ? '–' : t(entry.accepted ? 'yes' : 'no')}</td>
                <td style={td}>
                  <span style={{
                    background: CHIP[entry.outcome][0], color: CHIP[entry.outcome][1], borderRadius: 12, padding: '2px 10px',
                    fontSize: 13, fontFamily: BOLD,
                  }}
                  >
                    {t('outcome_' + entry.outcome)}
                  </span>
                </td>
                <td style={{ ...td, color: entry.button_ms > 2000 ? AMBER : '#222' }}>{seconds(entry.button_ms)}</td>
                <td style={td}>{entry.query_s === null ? '–' : entry.query_s + ' s'}</td>
                <td style={td}>{entry.opened_at ? shortTime(entry.opened_at) : '–'}</td>
                <td style={td}>{entry.gap_min === null ? '–' : entry.gap_min + ' min'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 13, color: '#5F6B76', lineHeight: '20px', marginTop: 20 }}>{t('helpEzyvinLog')}</p>
    </div>
  );
}

export default function EzyvinTab({
  data, t, onWorkshop, onPeriod,
}) {
  if (data.workshop) {
    return <EzyvinMonth data={data} t={t} onWorkshop={onWorkshop} />;
  }
  return <EzyvinList data={data} t={t} onWorkshop={onWorkshop} onPeriod={onPeriod} />;
}
